import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Upload,
  message,
  Icon,
  Progress,
  Select,
  Button,
  Card,
  Tag,
  Divider,
  Alert,
} from 'antd';
import { AppStoreContext } from '../../context/appStoreContext';
import { getAllTeams } from '../../utils/services/teamsAPI';
import { getAllCategories } from '../../utils/services/categoriesAPI';
import { getAllVariables } from '../../utils/services/variablesAPI';
import { getAllBlockElements } from '../../utils/services/elementAPI';
import {
  importSharedResources,
  importSections,
  importCommunication,
  exportMapCache,
  importMapCache,
  parseBulkImportZip,
} from '../../utils/bulkDocumentImportService';
import { BulkUploadButton } from '../TemplateSection/styles';
import './styles.css';

const { Option } = Select;

const BulkDocumentImportPage = () => {
  const { globalVariables } = React.useContext(AppStoreContext);
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [sectionCategories, setSectionCategories] = useState([]);
  const [selectedDocumentCategoryId, setSelectedDocumentCategoryId] = useState(null);
  const [selectedSectionCategoryId, setSelectedSectionCategoryId] = useState(null);
  const [phase1Result, setPhase1Result] = useState(null);
  const [phase2Progress, setPhase2Progress] = useState(null);
  const [sectionStatuses, setSectionStatuses] = useState({});
  const [phase3Progress, setPhase3Progress] = useState(null);
  const [documentStatuses, setDocumentStatuses] = useState({});
  const [isImporting, setIsImporting] = useState(false);
  const [variableIdMap, setVariableIdMap] = useState(null);
  const [imageUrlMap, setImageUrlMap] = useState(null);
  const [blockIdMap, setBlockIdMap] = useState(null);
  const [parsedZip, setParsedZip] = useState(null);
  const [mapFile, setMapFile] = useState(null);

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const response = await getAllTeams();
        if (response?.data) {
          setTeams(response.data);
          if (response.data.length === 1) setSelectedTeamId(response.data[0].rgId);
        }
      } catch (e) {
        console.error(e);
        message.error('Failed to load teams');
      }
    };
    const fetchCategories = async () => {
      try {
        const [templateRes, elementRes] = await Promise.all([
          getAllCategories(false, 'template'),
          getAllCategories(false, 'element'),
        ]);
        if (templateRes?.statusCode === 507 && templateRes?.data) {
          setCategories(templateRes.data);
          if (templateRes.data.length === 1) setSelectedDocumentCategoryId(templateRes.data[0].id);
        }
        if (elementRes?.statusCode === 507 && elementRes?.data) {
          setSectionCategories(elementRes.data);
          if (elementRes.data.length === 1) setSelectedSectionCategoryId(elementRes.data[0].id);
        }
      } catch (e) {
        console.error(e);
        message.error('Failed to load categories');
      }
    };
    fetchTeams();
    fetchCategories();
  }, []);

  const handleZipUpload = useCallback(
    async file => {
      if (!selectedTeamId) {
        message.error('Please select a team');
        return false;
      }
      if (!selectedDocumentCategoryId) {
        message.error('Please select a document category');
        return false;
      }
      if (!selectedSectionCategoryId) {
        message.error('Please select a section category');
        return false;
      }
      if (!file.name.endsWith('.zip')) {
        message.error('Please upload a ZIP file');
        return false;
      }

      setIsImporting(true);
      setPhase1Result(null);
      setPhase2Progress(null);
      setSectionStatuses({});
      setPhase3Progress(null);
      setDocumentStatuses({});
      setParsedZip(null);

      const arrayBuffer = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = e => resolve(e.target.result);
        reader.onerror = e => reject(e);
        reader.readAsArrayBuffer(file);
      });

      let vMap = variableIdMap;
      let iMap = imageUrlMap;
      let bMap = blockIdMap;

      try {
        const parsed = parseBulkImportZip(arrayBuffer);
        setParsedZip(parsed);

        if (!mapFile) {
          const gv = globalVariables && globalVariables.length > 0 ? globalVariables : (await getAllVariables())?.data || [];
          const onProgress = p => setPhase1Result(prev => ({ ...prev, progress: p }));
          const phase1 = await importSharedResources(arrayBuffer, gv, onProgress);
          vMap = phase1.variableIdMap;
          iMap = phase1.imageUrlMap;
          setVariableIdMap(vMap);
          setImageUrlMap(iMap);
          setPhase1Result({
            counts: phase1.counts,
            variableIdMap: vMap,
            imageUrlMap: iMap,
          });
        } else {
          vMap = mapFile.variableIdMap || new Map();
          iMap = mapFile.imageUrlMap || new Map();
          bMap = mapFile.blockIdMap || new Map();
          setVariableIdMap(vMap);
          setImageUrlMap(iMap);
          setBlockIdMap(bMap);
          setPhase1Result({ skipped: true, variableIdMap: vMap, imageUrlMap: iMap });
        }

        const { data: allBlocks } = await getAllBlockElements(1, false, selectedSectionCategoryId, undefined, undefined, 'write', false, false, {}, 5000)

        if (!mapFile && parsed.sectionFolders?.length > 0) {
          const onProgress = p => {
            setPhase2Progress(p);
            if (p.folderName)
              setSectionStatuses(prev => ({ ...prev, [p.folderName]: { ...prev[p.folderName], ...p } }));
          };
          console.log("allBlocks :: ", allBlocks);
          const newBlockIdMap = await importSections(
            arrayBuffer,
            vMap,
            iMap,
            selectedTeamId,
            selectedSectionCategoryId,
            onProgress,
            allBlocks
          );
          bMap = newBlockIdMap;
          setBlockIdMap(bMap);
          console.log("blockMap :: ", bMap);
        }

        if (parsed.documentFolders?.length > 0) {
          for (let d = 0; d < parsed.documentFolders.length; d++) {
            const folderName = parsed.documentFolders[d];
            setPhase3Progress({ current: folderName, index: d + 1, total: parsed.documentFolders.length });
            setDocumentStatuses(prev => ({ ...prev, [folderName]: { status: 'importing' } }));
            const langFiles = parsed.documentLangFiles[folderName] || {};
            const onProgress = p => setDocumentStatuses(prev => ({ ...prev, [folderName]: { ...prev[folderName], ...p } }));
            try {
              const result = await importCommunication(
                folderName,
                langFiles,
                vMap,
                iMap,
                bMap,
                selectedTeamId,
                selectedDocumentCategoryId,
                onProgress,
              );
              setDocumentStatuses(prev => ({
                ...prev,
                [folderName]: { ...prev[folderName], status: 'done', result },
              }));
            } catch (e) {
              setDocumentStatuses(prev => ({
                ...prev,
                [folderName]: { status: 'error', error: e?.message || String(e) },
              }));
            }
          }
          setPhase3Progress(null);
        }

        message.success('Bulk import finished');
      } catch (error) {
        console.error(error);
        message.error(error?.message || 'Bulk import failed');
      } finally {
        setIsImporting(false);
      }
      return false;
    },
    [
      selectedTeamId,
      selectedDocumentCategoryId,
      selectedSectionCategoryId,
      globalVariables,
      mapFile,
      variableIdMap,
      imageUrlMap,
      blockIdMap,
    ],
  );

  const handleExportMap = useCallback(() => {
    const vMap = variableIdMap || new Map();
    const iMap = imageUrlMap || new Map();
    const bMap = blockIdMap || new Map();
    const json = exportMapCache(vMap, iMap, bMap);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk-import-map.json';
    a.click();
    URL.revokeObjectURL(url);
    message.success('Map exported');
  }, [variableIdMap, imageUrlMap, blockIdMap]);

  const handleMapFileUpload = useCallback(file => {
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const data = importMapCache(e.target.result);
        if (data) {
          setMapFile(data);
          setVariableIdMap(data.variableIdMap);
          setImageUrlMap(data.imageUrlMap);
          setBlockIdMap(data.blockIdMap);
          message.success('Map loaded. You can skip Phase 1 & 2 by uploading a ZIP.');
        } else {
          message.error('Invalid map file');
        }
      } catch (err) {
        message.error('Failed to parse map file');
      }
    };
    reader.readAsText(file);
    return false;
  }, []);

  const hasMaps = (variableIdMap && variableIdMap.size > 0) || (blockIdMap && blockIdMap.size > 0);

  return (
    <div className="bulk-document-import-page">
      <Card title="Bulk Document Import" style={{ marginBottom: 20 }}>
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>Team</label>
          <Select
            style={{ width: '100%', maxWidth: 400 }}
            placeholder="Select team"
            value={selectedTeamId}
            onChange={setSelectedTeamId}
            disabled={isImporting}
          >
            {teams.map(t => (
              <Option key={t.rgId || t.id} value={t.rgId || t.id}>
                {t.groupName || t.name || `Team ${t.rgId || t.id}`}
              </Option>
            ))}
          </Select>
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>Document Category</label>
          <Select
            style={{ width: '100%', maxWidth: 400 }}
            placeholder="Category for documents"
            value={selectedDocumentCategoryId}
            onChange={setSelectedDocumentCategoryId}
            disabled={isImporting}
          >
            {categories.map(c => (
              <Option key={c.id} value={c.id}>
                {c.name || `Category ${c.id}`}
              </Option>
            ))}
          </Select>
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>Section Category</label>
          <Select
            style={{ width: '100%', maxWidth: 400 }}
            placeholder="Category for standalone blocks (sections, type: element)"
            value={selectedSectionCategoryId}
            onChange={setSelectedSectionCategoryId}
            disabled={isImporting}
          >
            {sectionCategories.map(c => (
              <Option key={c.id} value={c.id}>
                {c.name || `Category ${c.id}`}
              </Option>
            ))}
          </Select>
        </div>

        <Divider orientation="left">Map cache</Divider>
        <p style={{ marginBottom: 10, color: '#666' }}>
          Export maps after Phase 1 (and optionally Phase 2), then re-upload the map file to skip resource/section import and only run document import.
        </p>
        <div style={{ marginBottom: 20, display: 'flex', gap: 10 }}>
          <Button onClick={handleExportMap} disabled={!hasMaps || isImporting}>
            Export Map
          </Button>
          <Upload accept=".json" showUploadList={false} beforeUpload={handleMapFileUpload} disabled={isImporting}>
            <Button disabled={isImporting}>Import Map</Button>
          </Upload>
          {mapFile && (
            <Tag color="green">Map loaded (variableIdMap, imageUrlMap, blockIdMap)</Tag>
          )}
        </div>

        <Divider orientation="left">ZIP upload</Divider>
        <Alert
          message="ZIP structure"
          description="ZIP must contain: variables.json, images.json, relationships.json at top level; sections/ with subfolders (each with en.json, de.json, etc.); documents/ with subfolders (each with en.json, de.json, etc.)."
          type="info"
          showIcon
          style={{ marginBottom: 20 }}
        />
        <Upload
          accept=".zip"
          showUploadList={false}
          beforeUpload={handleZipUpload}
          disabled={!selectedTeamId || !selectedDocumentCategoryId || !selectedSectionCategoryId || isImporting}
        >
          <BulkUploadButton style={{ margin: 'auto', width: '50%' }}>
            <p className={isImporting ? 'downloadZip' : ''} style={{ visibility: isImporting ? 'visible' : 'hidden' }}>
              <Icon type="loading" style={{ marginRight: 5, fontSize: 20, color: '#FC6E51' }} />
            </p>
            <p className="import">{isImporting ? 'Importing...' : 'Upload ZIP'}</p>
            <p className="desc">
              {isImporting
                ? 'Please wait'
                : !selectedTeamId || !selectedDocumentCategoryId || !selectedSectionCategoryId
                ? 'Select team and both categories'
                : 'Select a ZIP file to run bulk import'}
            </p>
          </BulkUploadButton>
        </Upload>

        {phase1Result && !phase1Result.skipped && phase1Result.counts && (
          <div style={{ marginTop: 24 }}>
            <h3>Phase 1: Shared resources</h3>
            <Card size="small">
              <div>Relationships: created {phase1Result.counts.relationships?.created ?? 0}, skipped {phase1Result.counts.relationships?.skipped ?? 0}</div>
              <div>Variables: created {phase1Result.counts.variables?.created ?? 0}, skipped {phase1Result.counts.variables?.skipped ?? 0}</div>
              <div>Images: created {phase1Result.counts.images?.created ?? 0}, skipped {phase1Result.counts.images?.skipped ?? 0}</div>
            </Card>
          </div>
        )}
        {phase1Result?.skipped && (
          <div style={{ marginTop: 24 }}>
            <Tag color="orange">Phase 1 skipped (using imported map)</Tag>
          </div>
        )}

        {phase2Progress && parsedZip?.sectionFolders?.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <h3>Phase 2: Standalone blocks (sections)</h3>
            <Progress
              percent={
                parsedZip.sectionFolders.length
                  ? Math.round(((phase2Progress.sectionIndex ?? 0) / parsedZip.sectionFolders.length) * 100)
                  : 0
              }
              status="active"
            />
            <p>
              {phase2Progress.folderName && `Section: ${phase2Progress.folderName}`}
            </p>
          </div>
        )}

        {phase3Progress && (
          <div style={{ marginTop: 24 }}>
            <h3>Phase 3: Documents</h3>
            <Progress
              percent={phase3Progress.total ? Math.round((phase3Progress.index / phase3Progress.total) * 100) : 0}
              status="active"
            />
            <p>Importing: {phase3Progress.current}</p>
          </div>
        )}

        {Object.keys(documentStatuses).length > 0 && !isImporting && (
          <div style={{ marginTop: 24 }}>
            <h3>Document results</h3>
            {Object.entries(documentStatuses).map(([folder, status]) => (
              <Card key={folder} size="small" style={{ marginTop: 8 }}>
                <strong>{folder}</strong>
                {status.status === 'done' && status.result && (
                  <div style={{ marginTop: 8 }}>
                    {Object.entries(status.result.languages || {}).map(([lang, res]) => (
                      <div key={lang} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <Tag>{lang.toUpperCase()}</Tag>
                        {res.success ? (
                          <>
                            <Tag color="green">Success</Tag>
                            {res.communicationId && <span>CommunicationId: {res.communicationId}</span>}
                          </>
                        ) : (
                          <Tag color="red">{res.error}</Tag>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {status.status === 'error' && <Tag color="red">{status.error}</Tag>}
              </Card>
            ))}
          </div>
        )}

        <Divider orientation="left">Export</Divider>
        <p style={{ marginBottom: 10, color: '#666' }}>
          <Link to="/bulk-document-export">Export documents and sections</Link> from selected categories (including multiple section categories) into a ZIP.
        </p>
      </Card>
    </div>
  );
};

export default BulkDocumentImportPage;
